import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, House, LayoutTemplate, PanelLeftClose, PanelLeftOpen, Plus, Search, Settings, Star, Trash2, Users, type LucideIcon } from 'lucide-react';
import { PRODUCT_NAME } from '@os/shared';
import { useApi } from '../../../app/providers';
import { href, Link, matchRoute, navigate, useLocation } from '../../../app/router';
import type { Org } from '../../../lib/api';
import { Button, Dialog, IconButton, Input, Logo, Menu, Select, Tooltip } from '../../../ui';
import { cn } from '../../../ui/cn';
import { focusRing } from '../../../ui/focus';
import { Icon } from '../../../ui/icons';
import { NotificationsBell } from '../../comments/NotificationsBell';
import { NewBoardDialog } from '../../templates/NewBoardDialog';
import { NameDialog } from '../dialogs';
import { useInbox, useInvalidateContent, useOrg, useProjects, withInbox } from '../queries';
import { useSharedWithMe } from '../SharedPage';
import { AccountMenu } from './AccountMenu';
import { ProjectTree } from './ProjectTree';

type NavItem = { to: string; key: string; icon: LucideIcon };

const link = cn(
  'flex items-center gap-2.5 rounded-full px-3 py-2 font-sans text-sm text-fg-muted os-press hover:bg-chrome-hover hover:text-fg aria-[current=page]:bg-chrome aria-[current=page]:font-medium aria-[current=page]:text-fg pointer-coarse:min-h-11',
  focusRing,
);

const COLLAPSED_KEY = 'os.sidebar.collapsed';

/** Folded or not, remembered in this browser (a convenience: it may be lost). */
export function useCollapsed(): [boolean, (on: boolean) => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const set = (on: boolean) => {
    setCollapsed(on);
    try {
      localStorage.setItem(COLLAPSED_KEY, String(on));
    } catch {
      // Blocked storage: folded for this visit only.
    }
  };
  return [collapsed, set];
}

/**
 * "New board…" from anywhere: the project first (the open one by default, « Sans projet » when none is open, P17),
 * then the usual dialog.
 */
function NewBoardFlow({ org, onClose }: { org: Org; onClose: () => void }) {
  const { t } = useTranslation();
  const { path, search } = useLocation();
  const projects = useProjects(org.id);
  const inbox = useInbox(org.id);
  const route = matchRoute(path, search);
  const choices = withInbox(
    inbox.data,
    t('dashboard.noProject'),
    (projects.data ?? []).filter((p) => !p.archivedAt),
  );
  const current = route?.params.projectId && choices.some((p) => p.id === route.params.projectId) ? route.params.projectId : undefined;
  const [projectId, setProjectId] = useState(current ?? '');
  const [chosen, setChosen] = useState<string | null>(null);
  if (chosen) return <NewBoardDialog org={org} projectId={chosen} folderId={route?.params.folderId && chosen === current ? route.params.folderId : null} onClose={onClose} />;
  const value = projectId || inbox.data || choices[0]?.id || '';
  return (
    <Dialog
      title={t('dashboard.newBoard')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('dashboard.cancel')}</Button>
          <Button variant="primary" disabled={!value} onClick={() => setChosen(value)}>
            {t('dashboard.sidebar.continue')}
          </Button>
        </>
      }
    >
      <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
        {t('dashboard.project')}
        <Select value={value} onChange={(e) => setProjectId(e.target.value)}>
          {choices.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </label>
    </Dialog>
  );
}

/**
 * The dashboard's sidebar (P16-4 Ruling 7): search, quick create, sections, the project tree (drop targets), the
 * account at the foot; folds to icons with tooltips. Externals see "Shared with me" and Settings.
 */
export function Sidebar({ collapsed, onToggle, inSheet = false }: { collapsed: boolean; onToggle?: () => void; inSheet?: boolean }) {
  const { t } = useTranslation();
  const api = useApi();
  const { path, search } = useLocation();
  const { org } = useOrg();
  const shared = useSharedWithMe();
  const projects = useProjects(org?.id);
  const invalidate = useInvalidateContent();
  const [q, setQ] = useState(() => (path === href('search') ? (new URLSearchParams(search).get('q') ?? '') : ''));
  const [creating, setCreating] = useState<'board' | 'project' | null>(null);
  const showShared = !org || (shared.data?.length ?? 0) > 0;

  const top: NavItem[] = org
    ? [
        { to: href('home'), key: 'dashboard.home', icon: House },
        { to: href('recent'), key: 'dashboard.recent', icon: Clock },
        { to: href('favorites'), key: 'dashboard.favorites', icon: Star },
        ...(showShared ? [{ to: href('shared'), key: 'dashboard.shared', icon: Users }] : []),
      ]
    : [{ to: href('shared'), key: 'dashboard.shared', icon: Users }];
  const bottom: NavItem[] = [
    ...(org
      ? [
          { to: href('templates'), key: 'dashboard.templates', icon: LayoutTemplate },
          { to: href('trash'), key: 'dashboard.trash', icon: Trash2 },
        ]
      : []),
    { to: href('settings'), key: 'dashboard.settings', icon: Settings },
  ];

  const item = ({ to, key, icon }: NavItem) => {
    const current = path === to || (to === href('settings') && path.startsWith(`${to}/`)) ? 'page' : undefined;
    const anchor = (
      <Link to={to} aria-current={current} aria-label={collapsed ? t(key) : undefined} className={cn(link, collapsed && 'justify-center px-0')}>
        <Icon icon={icon} size={18} />
        {!collapsed && t(key)}
      </Link>
    );
    return <li key={to}>{collapsed ? <Tooltip label={t(key)} side="right">{anchor}</Tooltip> : anchor}</li>;
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (q.trim()) navigate(href('search', {}, { q: q.trim() }));
  };

  return (
    <nav
      aria-label={t('dashboard.navigation')}
      data-collapsed={String(collapsed)}
      className={cn('flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain', !inSheet && 'p-3')}
    >
      <div className={cn('flex items-center gap-1', collapsed ? 'flex-col' : 'justify-between pl-1')}>
        {collapsed ? (
          <Logo size={24} title={PRODUCT_NAME} className="m-1.5 text-fg" />
        ) : (
          <p className="flex min-w-0 items-center gap-2 font-display text-base font-semibold text-fg">
            <Logo size={24} decorative className="text-fg" />
            <span className="min-w-0 leading-tight text-balance">{PRODUCT_NAME}</span>
          </p>
        )}
        <div className={cn('flex items-center gap-1', collapsed && 'flex-col')}>
          <NotificationsBell />
          {onToggle && (
            <IconButton
              label={collapsed ? t('dashboard.sidebar.expand') : t('dashboard.sidebar.collapse')}
              icon={<Icon icon={collapsed ? PanelLeftOpen : PanelLeftClose} size={18} />}
              onClick={onToggle}
            />
          )}
        </div>
      </div>

      {collapsed ? (
        <IconButton label={t('dashboard.search')} icon={<Icon icon={Search} size={18} />} onClick={onToggle} className="self-center" />
      ) : (
        <form role="search" onSubmit={submit} className="relative">
          <Icon icon={Search} size={16} className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-subtle" />
          <Input type="search" aria-label={t('dashboard.search')} placeholder={t('dashboard.searchPlaceholder')} className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
        </form>
      )}

      {org && (
        <Menu.Root>
          <Menu.Trigger
            aria-label={t('dashboard.sidebar.create')}
            className={cn(
              'flex h-10 items-center justify-center gap-2 rounded-full bg-accent px-4 font-sans text-sm font-medium text-accent-fg hover:bg-accent-hover pointer-coarse:min-h-11',
              collapsed && 'size-9 self-center px-0',
              focusRing,
            )}
          >
            <Icon icon={Plus} size={16} />
            {!collapsed && t('dashboard.sidebar.create')}
          </Menu.Trigger>
          <Menu.Popup label={t('dashboard.sidebar.create')}>
            <Menu.Item onClick={() => setCreating('board')}>{t('dashboard.sidebar.newBoard')}</Menu.Item>
            <Menu.Item onClick={() => setCreating('project')}>{t('dashboard.newProject')}</Menu.Item>
          </Menu.Popup>
        </Menu.Root>
      )}

      <ul className="flex flex-col gap-0.5">{top.map(item)}</ul>

      {org && !collapsed && (projects.data?.length ?? 0) > 0 && (
        <div className="flex flex-col gap-1">
          <p className="px-2.5 font-sans text-xs font-medium tracking-wide text-fg-muted uppercase">{t('dashboard.projects')}</p>
          <ProjectTree projects={projects.data ?? []} />
        </div>
      )}

      <ul className="flex flex-col gap-0.5">{bottom.map(item)}</ul>

      <div className="mt-auto pt-3">
        <AccountMenu collapsed={collapsed} />
      </div>

      {creating === 'board' && org && <NewBoardFlow org={org} onClose={() => setCreating(null)} />}
      {creating === 'project' && org && (
        <NameDialog
          title={t('dashboard.newProject')}
          label={t('dashboard.projectName')}
          submitLabel={t('dashboard.create')}
          max={120}
          onClose={() => setCreating(null)}
          onSubmit={async (name) => {
            const project = await api.createProject({ orgId: org.id, name });
            await invalidate();
            navigate(href('project', { projectId: project.id }));
          }}
        />
      )}
    </nav>
  );
}
